import { FC } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';

import { Logo } from '~/components/logo';
import { SectionCard } from '~/components/section-card';
import { SectionGroup } from '~/components/section-group';
import { supabase } from '~/libs/supabase';

export const newPasswordSchema = z.object({
  password: z.string(),
});

export type NewPassword = z.infer<typeof newPasswordSchema>;

export const NewPasswordForm: FC<{ accessToken: string }> = ({ accessToken }) => {
  const router = useRouter();

  const toast = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm({
    resolver: zodResolver(newPasswordSchema),
    shouldUseNativeValidation: false,
    reValidateMode: 'onChange',
    mode: 'onChange',
    defaultValues: {
      password: '',
    } as NewPassword,
  });

  const onSubmit = async (values: NewPassword) => {
    const { error } = await supabase.auth.api.updateUser(accessToken, { password: values.password });

    if (error) {
      toast({
        title: 'An error occurred',
        description: error.message ? error.message : 'An error occurred, please try again',
        status: 'error',
        isClosable: true,
      });

      console.error(error);
    } else {
      toast({
        title: 'Success',
        description: 'You remembered your password!',
        status: 'success',
        isClosable: true,
      });

      router.push('/log-in');
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <SectionCard
        title='Enter new password'
        footerSlot={<SectionFooter isValid={isValid} isSubmitting={isSubmitting} />}
      >
        <Stack spacing={8}>
          <SectionGroup>
            <Stack spacing={4}>
              <FormControl isInvalid={Boolean(errors.password)}>
                <FormLabel htmlFor='password'>Enter new password</FormLabel>
                <Box maxW='sm'>
                  <Input
                    id='password'
                    type='password'
                    {...register('password', {
                      required: 'Password is required',
                    })}
                  />
                </Box>
                <FormErrorMessage>
                  <>{errors.password?.message}</>
                </FormErrorMessage>
              </FormControl>
            </Stack>
          </SectionGroup>
        </Stack>
      </SectionCard>
    </form>
  );
};

const SectionFooter: FC<{ isValid: boolean; isSubmitting: boolean }> = ({ isValid, isSubmitting }) => {
  return (
    <Flex justifyContent='space-between' gap={4} flexDir={{ base: 'column', lg: 'row' }}>
      <Logo />
      <Flex alignItems='center' gap={4} flexDir={{ base: 'column-reverse', lg: 'row' }}>
        <Text fontSize='xs'>
          <Link href='/sign-up'>
            <a>
              <Text as='span' fontSize='xs' fontWeight='bold' textDecor='underline'>
                Go back to log in
              </Text>
            </a>
          </Link>
        </Text>
        <Button isLoading={isSubmitting} disabled={!isValid || isSubmitting} type='submit'>
          Reset password
        </Button>
      </Flex>
    </Flex>
  );
};
